import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { CommonSellerService } from '@savgroup-front-common/core/api/Seller';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';

import { adaptCompanyProfileToActivities } from '../adapters/adaptCompanyProfileToActivities';
import {
  PickedCompany,
  ProposedActivityProfile,
} from '../OnboardingActivities.types';

export const COMPANY_PROFILE_QUERY_KEY = 'suggestCompanyProfile';

interface UseCompanyProfileSuggestionReturnValues {
  proposedProfile: ProposedActivityProfile;
  isLoading: boolean;
  hasSuggestionFailed: boolean;
  hasPickedCompany: boolean;
  website?: string;
  isWebsiteVerified: boolean;
}

export function useCompanyProfileSuggestion({
  pickedCompany,
  email,
}: {
  pickedCompany?: PickedCompany;
  email?: string;
}): UseCompanyProfileSuggestionReturnValues {
  const locale = useSelector(selectLocale);

  const { data, isLoading } = useQuery(
    [COMPANY_PROFILE_QUERY_KEY, { pickedCompany, locale, email }],
    async () => {
      if (!pickedCompany) {
        return {
          proposedProfile: adaptCompanyProfileToActivities({}),
          hasFailed: false,
          website: undefined,
          isWebsiteVerified: false,
        };
      }

      const response = await CommonSellerService.suggestCompanyProfileCommand({
        source: pickedCompany.source,
        registrationNumber: pickedCompany.registrationNumber,
        language: locale,
        email,
      });

      if (response.failure) {
        return {
          proposedProfile: adaptCompanyProfileToActivities({}),
          hasFailed: true,
          website: undefined,
          isWebsiteVerified: false,
        };
      }

      return {
        proposedProfile: adaptCompanyProfileToActivities({
          suggestion: response.value,
        }),
        hasFailed: false,
        website: response.value.website || undefined,
        isWebsiteVerified: response.value.websiteVerified ?? false,
      };
    },
    { enabled: !!pickedCompany, staleTime: Infinity },
  );

  return {
    proposedProfile:
      data?.proposedProfile ?? adaptCompanyProfileToActivities({}),
    isLoading,
    hasSuggestionFailed: data?.hasFailed ?? false,
    hasPickedCompany: !!pickedCompany,
    website: data?.website,
    isWebsiteVerified: data?.isWebsiteVerified ?? false,
  };
}
